/**
 * 国际化上下文 - 创美药业设计体系
 * 
 * 功能特性：
 * - 支持简体中文、繁体中文、英文
 * - 集成 react-i18next
 * - 本地存储语言偏好
 * - 动态语言切换
 * - 与 Ant Design 国际化集成
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import zhCN from 'antd/locale/zh_CN';
import zhTW from 'antd/locale/zh_TW';
import enUS from 'antd/locale/en_US';

// 支持的语言类型
export type SupportedLanguage = 'zh-CN' | 'zh-TW' | 'en';

// 语言配置映射
export const LANGUAGE_CONFIG = {
  'zh-CN': {
    label: '简体中文',
    antdLocale: zhCN,
    flag: '🇨🇳',
  },
  'zh-TW': {
    label: '繁體中文',
    antdLocale: zhTW,
    flag: '🇹🇼',
  },
  'en': {
    label: 'English',
    antdLocale: enUS,
    flag: '🇺🇸',
  },
} as const;

// 国际化上下文类型定义
interface I18nContextType {
  /** 当前语言 */
  language: SupportedLanguage;
  /** 切换语言 */
  changeLanguage: (lang: SupportedLanguage) => void;
  /** 获取语言配置 */
  getLanguageConfig: (lang: SupportedLanguage) => typeof LANGUAGE_CONFIG[SupportedLanguage];
  /** 所有支持的语言 */
  supportedLanguages: SupportedLanguage[];
  /** 翻译函数 */
  t: (key: string, options?: Record<string, unknown>) => string;
}

// 创建国际化上下文
export const I18nContext = createContext<I18nContextType | undefined>(undefined);

// 国际化 Provider 属性
interface I18nProviderProps {
  children: ReactNode;
  /** 默认语言 */
  defaultLanguage?: SupportedLanguage;
}

// 本地存储键名
const LANGUAGE_STORAGE_KEY = 'changmei-language';

/**
 * 获取浏览器语言偏好
 */
const getBrowserLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') return 'zh-CN';
  
  const browserLang = navigator.language || navigator.languages?.[0];
  
  if (browserLang?.startsWith('zh')) {
    // 检测是否为繁体中文
    if (browserLang.includes('TW') || browserLang.includes('HK') || browserLang.includes('MO')) {
      return 'zh-TW';
    }
    return 'zh-CN';
  }
  
  if (browserLang?.startsWith('en')) {
    return 'en';
  }
  
  return 'zh-CN'; // 默认简体中文
};

/**
 * 获取存储的语言偏好
 */
const getStoredLanguage = (): SupportedLanguage | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && Object.keys(LANGUAGE_CONFIG).includes(stored)) {
      return stored as SupportedLanguage;
    }
  } catch {
    // 忽略存储错误
  }
  
  return null;
};

/**
 * 存储语言偏好
 */
const storeLanguage = (language: SupportedLanguage): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // 忽略存储错误
  }
};

/**
 * 国际化 Provider 组件
 * 
 * 提供语言状态管理和 Ant Design 国际化配置
 */
export const I18nProvider: React.FC<I18nProviderProps> = ({ 
  children, 
  defaultLanguage = 'zh-CN' 
}) => {
  const { i18n, t } = useTranslation();
  
  // 初始化语言状态
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    // 优先级：存储的语言 > 浏览器语言 > 默认语言
    return getStoredLanguage() || getBrowserLanguage() || defaultLanguage;
  });

  // 初始化 i18next 语言
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // 切换语言
  const changeLanguage = (newLanguage: SupportedLanguage) => {
    setLanguageState(newLanguage);
    storeLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  // 获取语言配置
  const getLanguageConfig = (lang: SupportedLanguage) => {
    return LANGUAGE_CONFIG[lang];
  };

  // 支持的语言列表
  const supportedLanguages: SupportedLanguage[] = Object.keys(LANGUAGE_CONFIG) as SupportedLanguage[];

  // 当前语言的 Ant Design 国际化配置
  const currentAntdLocale = LANGUAGE_CONFIG[language].antdLocale;

  // 国际化上下文值
  const contextValue: I18nContextType = {
    language,
    changeLanguage,
    getLanguageConfig,
    supportedLanguages,
    t,
  };

  return (
    <I18nContext.Provider value={contextValue}>
      <ConfigProvider locale={currentAntdLocale}>
        {children}
      </ConfigProvider>
    </I18nContext.Provider>
  );
};

/**
 * 使用国际化的 Hook
 * 
 * @returns 国际化上下文值
 * @throws 如果在 I18nProvider 外部使用则抛出错误
 */
export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  
  return context;
};

/**
 * 语言切换器 Hook
 * 
 * 提供简化的语言切换功能
 */
export const useLanguageSwitch = () => {
  const { language, changeLanguage, getLanguageConfig, supportedLanguages } = useI18n();
  
  return {
    /** 当前语言 */
    currentLanguage: language,
    /** 切换到指定语言 */
    switchTo: changeLanguage,
    /** 切换到简体中文 */
    switchToZhCN: () => changeLanguage('zh-CN'),
    /** 切换到繁体中文 */
    switchToZhTW: () => changeLanguage('zh-TW'),
    /** 切换到英文 */
    switchToEn: () => changeLanguage('en'),
    /** 获取语言配置 */
    getConfig: getLanguageConfig,
    /** 所有支持的语言 */
    allLanguages: supportedLanguages,
    /** 当前语言配置 */
    currentConfig: getLanguageConfig(language),
  };
};

// 导出语言相关类型
export type { I18nContextType, I18nProviderProps };